// client/src/coms/serveradmin/ServerAdmin.js
// Server Settings: one place to administer a server. The sections shown come from the
// server (GET /servers/:key/admin), which lists only those the member may open; every action is checked again
// by its own route, so hiding a section is never the only protection.
import React, { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import '../organisation/org.css';
import './admin.css';
import { useLoad, Empty, keyOf } from './adminKit';
import { pickServerKey } from '../components/serversettings_parts/serverSettingsUtils';

const SECTIONS = {
  overview: lazy(() => import('./sections/OverviewSection')),
  roles: lazy(() => import('./sections/RolesSection')),
  organisation: lazy(() => import('./sections/OrganisationSection')),
  channels: lazy(() => import('./sections/ChannelsSection')),
  import: lazy(() => import('./sections/ImportSection')),
  members: lazy(() => import('./sections/MembersSection')),
  invites: lazy(() => import('./sections/InvitesSection')),
  moderation: lazy(() => import('./sections/ModerationSection')),
  safety: lazy(() => import('./sections/SafetySection')),
  automations: lazy(() => import('./sections/AutomationsSection')),
  onboarding: lazy(() => import('./sections/OnboardingSection')),
  announcements: lazy(() => import('./sections/AnnouncementsSection')),
  audit: lazy(() => import('./sections/AuditSection')),
  privacy: lazy(() => import('./sections/PrivacySection')),
  insights: lazy(() => import('./sections/InsightsSection')),
  integrations: lazy(() => import('./sections/IntegrationsSection')),
  assets: lazy(() => import('./sections/AssetsSection')),
  ownership: lazy(() => import('./sections/OwnershipSection')),
};

const ICONS = {
  overview: '🏠',
  roles: '🛡️',
  organisation: '#',
  channels: '🔐',
  import: '📥',
  members: '👥',
  invites: '✉️',
  moderation: '⚖️',
  safety: '🚨',
  automations: '⚙️',
  onboarding: '👋',
  announcements: '📢',
  audit: '📜',
  privacy: '🔒',
  insights: '📈',
  integrations: '🔌',
  assets: '🎨',
  ownership: '👑',
};

const GROUPS = [
  { label: 'Server', keys: ['overview', 'roles', 'organisation', 'channels', 'invites', 'assets'] },
  { label: 'Community', keys: ['members', 'onboarding', 'announcements', 'insights'] },
  { label: 'Safety', keys: ['moderation', 'safety', 'automations', 'audit', 'privacy'] },
  { label: 'Administration', keys: ['import', 'integrations', 'ownership'] },
];

const STORE = 'vx-server-admin-section';

export default function ServerAdmin({ server, user, apiAvailable, onClose, onRenamed, onDeleted, initialSection }) {
  const serverKey = pickServerKey(server);
  const { data, err, reload } = useLoad(serverKey ? `/servers/${keyOf(serverKey)}/admin` : '');
  const [active, setActive] = useState(() => {
    if (initialSection) return initialSection;
    try {
      return localStorage.getItem(STORE) || 'overview';
    } catch {
      return 'overview';
    }
  });
  const [mobileNav, setMobileNav] = useState(false);
  const [filter, setFilter] = useState('');
  // Every visit to a section (also clicking the current one again) shows fresh data.
  const [visit, setVisit] = useState(0);

  const sections = useMemo(() => (data && data.sections) || [], [data]);
  const allowed = useMemo(() => new Set(sections.map((s) => s.key)), [sections]);
  const current = allowed.has(active) ? active : sections.length ? sections[0].key : '';

  useEffect(() => {
    if (initialSection) setActive(initialSection);
  }, [initialSection]);

  // Other parts of the app can open a section: window.dispatchEvent(new CustomEvent('vox-open-server-admin', { detail: { section } }))
  useEffect(() => {
    const onOpen = (e) => {
      const s = e && e.detail && e.detail.section;
      if (s) {
        setActive(s);
        setVisit((v) => v + 1);
      }
    };
    window.addEventListener('vox-open-server-admin', onOpen);
    return () => window.removeEventListener('vox-open-server-admin', onOpen);
  }, []);

  const go = useCallback(
    (key) => {
      setActive(key);
      setVisit((v) => v + 1);
      setMobileNav(false);
      reload();
      try {
        localStorage.setItem(STORE, key);
      } catch {}
    },
    [reload]
  );

  const Section = current ? SECTIONS[current] : null;
  const ctx = {
    serverKey,
    server,
    user,
    apiAvailable,
    admin: data,
    permissions: new Set((data && data.permissions) || []),
    isOwner: !!(data && data.is_owner),
    can: (k) => !!(data && (data.is_owner || (data.permissions || []).includes(k))),
    reloadAdmin: reload,
    go,
    onRenamed,
    onDeleted,
    onClose,
  };

  return (
    <div className="vxa-root" data-testid="server-admin">
      <div className="vxo-head">
        <div className="vxo-head-title" style={{ minWidth: 0 }}>
          <h1>Server Settings</h1>
          <span className="vxo-head-sub">{String((server && server.name) || (data && data.server && data.server.name) || '')}</span>
          <span className="vxa-muted vxa-scope-note">
            Changes here affect everyone in this server.{' '}
            <button
              type="button"
              className="vx-link-btn"
              onClick={() => {
                try {
                  window.__voxPendingSettingsTab = 'server';
                  window.dispatchEvent(new CustomEvent('vox-open-settings', { detail: { tab: 'server' } }));
                } catch {}
              }}
              data-testid="admin-open-personal"
            >
              Your own preferences for it
            </button>{' '}
            are in Personal Settings.
          </span>
        </div>
        <div className="vxa-row">
          <button type="button" className="vx-btn small vxo-mobile-nav-btn" onClick={() => setMobileNav((v) => !v)} aria-expanded={mobileNav} aria-controls="vxa-nav">
            ☰ Sections
          </button>
          <button type="button" className="vx-btn" onClick={() => typeof onClose === 'function' && onClose()}>
            Done
          </button>
        </div>
      </div>
      {err && !data ? (
        <Empty icon="🔒" title="Server Settings are not available">
          {err}
        </Empty>
      ) : !data ? (
        <div className="vxo-loading" style={{ padding: 20 }}>
          Loading…
        </div>
      ) : (
        <div className={`vxo-split${mobileNav ? ' is-nav-open' : ''}`}>
          <nav className="vxo-nav" id="vxa-nav" aria-label="Server Settings sections">
            {sections.length > 6 ? (
              <input
                type="search"
                className="vxo-input vxa-nav-filter"
                placeholder="Find a section"
                aria-label="Find a Server Settings section"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  const q = filter.trim().toLowerCase();
                  const hit = sections.find((x) => x.label.toLowerCase().includes(q));
                  if (q && hit) {
                    go(hit.key);
                    setFilter('');
                  }
                }}
                data-testid="admin-nav-filter"
              />
            ) : null}
            {GROUPS.map((g) => {
              const q = filter.trim().toLowerCase();
              const items = g.keys.filter((k) => allowed.has(k) && (!q || (sections.find((x) => x.key === k) || { label: '' }).label.toLowerCase().includes(q)));
              if (!items.length) return null;
              return (
                <div key={g.label} style={{ marginBottom: 6 }}>
                  <div className="vxa-muted" style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '8px 10px 2px' }}>
                    {g.label}
                  </div>
                  {items.map((k) => {
                    const s = sections.find((x) => x.key === k);
                    const badge =
                      k === 'moderation' ? (data.overview.open_reports || 0) + (data.overview.open_appeals || 0) : k === 'members' ? data.overview.pending_members || 0 : k === 'safety' && (data.overview.emergency || data.overview.raid) ? '!' : 0;
                    return (
                      <button key={k} type="button" className={`vxo-nav-item${current === k ? ' is-active' : ''}`} aria-current={current === k ? 'page' : undefined} onClick={() => go(k)}>
                        <span className="vxo-nav-icon" aria-hidden="true">
                          {ICONS[k]}
                        </span>
                        <span className="vxo-nav-text">
                          <span className="vxo-nav-label">{s.label}</span>
                        </span>
                        {badge ? (
                          <span className="vxo-badge is-accent" style={{ marginLeft: 'auto' }} aria-label={`${badge} waiting`}>
                            {badge}
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </nav>
          <main className="vxo-main" aria-live="polite">
            {Section ? (
              <Suspense fallback={<div className="vxo-loading">Loading…</div>}>
                <Section key={`${serverKey}:${current}:${visit}`} {...ctx} />
              </Suspense>
            ) : (
              <Empty icon="🔒" title="Nothing to manage here">
                You do not have any management permissions in this server.
              </Empty>
            )}
          </main>
        </div>
      )}
    </div>
  );
}
